import { ref, toValue } from 'fine-jsx'
import type { MaybeRefOrGetter } from 'fine-jsx'

import { isElement, useEventListener } from 'shared/utils/index.ts'

export const useScrubber = (
  context: {
    pixelsPerSecond: MaybeRefOrGetter<number>
    currentTime: MaybeRefOrGetter<number>
    mediaDuration: MaybeRefOrGetter<number>
    offsetX?: MaybeRefOrGetter<number>
    seekTo: (timeS: number) => void
  },
  container: MaybeRefOrGetter<HTMLElement | undefined>,
  cursor: MaybeRefOrGetter<HTMLElement | undefined>,
  condition = (_event: PointerEvent) => true,
): void => {
  const isScrubbing = ref(false)

  const onScrubberDown = (event: PointerEvent): void => {
    if (
      isScrubbing.value ||
      !condition(event) ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (isElement(event.target) &&
        event.target.closest(
          'button,label:has(select,input),select,input,textarea,[contenteditable=true],dialog',
        ))
    )
      return

    const target = event.currentTarget as HTMLElement

    target.setPointerCapture(event.pointerId)
    isScrubbing.value = true
    onScrubberMove({
      offsetX: event.clientX - target.getBoundingClientRect().left,
    })
  }

  const onScrubberMove = ({ offsetX }: Pick<PointerEvent, 'offsetX'>): void => {
    if (!isScrubbing.value) return
    const time = (offsetX - (toValue(context.offsetX) ?? 0)) / toValue(context.pixelsPerSecond)
    context.seekTo(time)
  }
  const onScrubberUp = (): void => {
    isScrubbing.value = false
    toValue(cursor)?.focus()
  }

  useEventListener(container, 'pointerdown', onScrubberDown)
  useEventListener<PointerEvent>(container, 'pointermove', onScrubberMove)
  useEventListener(container, 'lostpointercapture', onScrubberUp)
}
